import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { CalendarType } from 'types';

dayjs.extend(utc);

const OUTPUT_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export const convertTimestampToDate = (timestamp: string | number) => {
  if (typeof timestamp === 'string') {
    const alreadyFormatted = dayjs(timestamp, OUTPUT_FORMAT, true);
    if (alreadyFormatted.isValid()) {
      return timestamp;
    }
  }

  let tsNum: number;

  if (typeof timestamp === 'number') {
    const len = timestamp.toString().length;
    if (len >= 11 && len <= 13) {
      tsNum = timestamp;
    } else {
      tsNum = timestamp * 1000;
    }
  } else if (/^\d{11,13}$/.test(timestamp)) {
    tsNum = parseInt(timestamp, 10);
  } else if (/^\d{1,10}$/.test(timestamp)) {
    tsNum = parseInt(timestamp, 10) * 1000;
  } else {
    const isoDate = dayjs.utc(timestamp);
    if (isoDate.isValid()) {
      return isoDate.format(OUTPUT_FORMAT);
    }

    throw new Error('Invalid timestamp format');
  }

  const d = dayjs.utc(tsNum);

  if (!d.isValid()) {
    throw new Error('Invalid date');
  }

  return d.format(OUTPUT_FORMAT);
};

const parseLocalDate = (date: string) =>
  dayjs(date, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'], true);

const toUtcDate = (
  date: string | number,
  tzOffset: number = 0
): dayjs.Dayjs | null => {
  if (typeof date === 'number') {
    return dayjs.unix(date).utc();
  }

  if (typeof date === 'string') {
    const parsed = parseLocalDate(date);
    if (!parsed.isValid()) return null;

    return parsed.utcOffset(tzOffset, true).utc();
  }

  return null;
};

const formatUtcDateForCalendar = (
  date: string | number,
  tzOffset: number = 0
): string => {
  const utcDate = toUtcDate(date, tzOffset);
  if (!utcDate) return '';

  return utcDate.format('YYYYMMDDTHHmmss[Z]');
};

const formatDateInTimeZone = (utcDate: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(utcDate);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';

  const y = get('year');
  const m = get('month');
  const d = get('day');
  const hh = get('hour');
  const mm = get('minute');
  const ss = get('second');

  return `${y}${m}${d}T${hh}${mm}${ss}`;
};

const offsetMinutesToEtcGmtTimeZone = (
  tzOffsetMinutes: number
): string | null => {
  if (!Number.isFinite(tzOffsetMinutes)) return null;
  if (tzOffsetMinutes % 60 !== 0) return null;
  const hours = tzOffsetMinutes / 60;
  const abs = Math.abs(hours);
  if (abs > 14) return null;
  const sign = hours <= 0 ? '+' : '-';
  return `Etc/GMT${sign}${abs}`;
};

const tzOffsetToGoogleTimeZone = (tzOffsetMinutes: number): string | null => {
  switch (tzOffsetMinutes) {
    case 0:
      return 'UTC';
    case -240:
      return 'America/Puerto_Rico';
    case -300:
      return 'America/New_York';
    case -360:
      return 'America/Chicago';
    case -420:
      return 'America/Denver';
    case -480:
      return 'America/Los_Angeles';
    case -540:
      return 'America/Anchorage';
    case -600:
      return 'Pacific/Honolulu';
    case -660:
      return 'Pacific/Pago_Pago';
    default:
      return null;
  }
};

export const getCalendarUrl = ({
  calendarType,
  eventTitle,
  eventStartDate,
  eventEndDate,
  eventDetails,
  location,
  tzOffset
}: {
  calendarType: CalendarType;
  eventTitle: string;
  eventStartDate: number | string;
  eventEndDate: number | string;
  eventDetails: string;
  location: string;
  tzOffset: number;
}) => {
  const startDate = formatUtcDateForCalendar(eventStartDate, tzOffset);
  const endDate = formatUtcDateForCalendar(eventEndDate, tzOffset);

  switch (calendarType) {
    case CalendarType.google:
      const googleTz =
        tzOffsetToGoogleTimeZone(tzOffset) ||
        offsetMinutesToEtcGmtTimeZone(tzOffset);
      const startUtc = toUtcDate(eventStartDate, tzOffset);
      const endUtc = toUtcDate(eventEndDate, tzOffset);
      if (!startUtc || !endUtc) return '';
      if (!googleTz) {
        return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
          eventTitle
        )}&dates=${startDate}/${endDate}&details=${encodeURIComponent(
          eventDetails
        )}&location=${encodeURIComponent(location)}`;
      }
      const googleStart = formatDateInTimeZone(startUtc.toDate(), googleTz);
      const googleEnd = formatDateInTimeZone(endUtc.toDate(), googleTz);
      return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
        eventTitle
      )}&dates=${googleStart}/${googleEnd}&ctz=${encodeURIComponent(
        googleTz
      )}&details=${encodeURIComponent(
        eventDetails
      )}&location=${encodeURIComponent(location)}`;

    case CalendarType.apple:
      const uid = `${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}@appointment.com`;

      const now = `${
        new Date().toISOString().replace(/[-:]/g, '').split('.')[0]
      }Z`;

      return `BEGIN:VCALENDAR
                VERSION:2.0
                PRODID:-//Appointment//Appointment//EN
                BEGIN:VEVENT
                UID:${uid}
                DTSTAMP:${now}
                DTSTART:${startDate}
                DTEND:${endDate}
                SUMMARY:${eventTitle.replace(/\n/g, '\\n')}
                DESCRIPTION:${eventDetails.replace(/\n/g, '\\n')}
                LOCATION:${location.replace(/\n/g, '\\n')}
                STATUS:CONFIRMED
                END:VEVENT
                END:VCALENDAR`;

    case CalendarType.yahoo:
      return `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(
        eventTitle
      )}&st=${startDate}&et=${endDate}&desc=${encodeURIComponent(
        eventDetails
      )}&in_loc=${encodeURIComponent(location)}`;

    case CalendarType.outlook:
      return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(
        eventTitle
      )}&startdt=${startDate}&enddt=${endDate}&body=${encodeURIComponent(
        eventDetails
      )}&location=${encodeURIComponent(location)}`;

    default:
      return '';
  }
};
